import React, { useMemo, useState, useEffect } from 'react';
import { 
    Dialog, DialogContent, IconButton, Typography, Grid, Box, Paper, Button, Checkbox, FormControlLabel
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon, MenuBook as BookIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// --- IMPORT ASSETS (Placeholder paths - ensure files exist in src/assets/) ---
import manualAdminImg from '../assets/manualadministrador.png';
import manualManagerImg from '../assets/manualgestor.png';
import manualCollabImg from '../assets/manualcolaborador.png';
import manualAdminPdf from '../assets/manualadministrador.pdf';
import manualManagerPdf from '../assets/manualgestor.pdf';
import manualCollabPdf from '../assets/manualcolaborador.pdf';

const MANUALS_DATA = [
    {
        id: 'admin',
        title: 'Manual do Administrador',
        description: 'Guia completo para configuração e gestão da plataforma.',
        coverUrl: manualAdminImg, 
        pdfUrl: manualAdminPdf,
        color: '#fca5a5' // Pastel Red
    },
    {
        id: 'manager',
        title: 'Manual do Gestor',
        description: 'Orientações para liderança, avaliações e gestão de equipe.',
        coverUrl: manualManagerImg,
        pdfUrl: manualManagerPdf,
        color: '#93c5fd' // Pastel Blue
    },
    {
        id: 'collaborator',
        title: 'Manual do Colaborador',
        description: 'Tudo sobre o seu desenvolvimento e uso do sistema.',
        coverUrl: manualCollabImg,
        pdfUrl: manualCollabPdf,
        color: '#86efac' // Pastel Green
    }
];

const ManualsModal = ({ isOpen, onClose, isAutoOpen = false }) => {
    const { user } = useAuth();
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [showWarning, setShowWarning] = useState(false);

    useEffect(() => {
        if (isAutoOpen && isOpen) {
            const hidden = localStorage.getItem('hideManualsModal');
            if (hidden === 'true') {
                onClose(); 
            }
        }
    }, [isAutoOpen, isOpen]);

    const handleCloseAttempt = () => {
        if (isAutoOpen) {
            if (dontShowAgain) {
                localStorage.setItem('hideManualsModal', 'true');
            }
            setShowWarning(true);
        } else {
            onClose();
        }
    };

    const confirmClose = () => {
        setShowWarning(false);
        onClose();
    };

    // --- LÓGICA DE PERMISSÃO ---
    const allowedManuals = useMemo(() => {
        if (!user) return [];

        const isAdminOrEditor = ['superadmin', 'admin', 'editor'].includes(user.role);

        const isManager = (() => {
            if (isAdminOrEditor) return true; 
            const u = user;
            const termo = 'vaga gerencial';
            const check = (f) => f && f.toString().toLowerCase().includes(termo);
            
            return (
                u.isGerencial || 
                u.is_gerencial === true || 
                check(u.jobDescription) || 
                check(u.job_description) || 
                check(u.cargo) || 
                (u.cargo && /gerente|lider|líder|coordena|supervisor|gestor/i.test(u.cargo))
            );
        })();

        return MANUALS_DATA.filter(manual => {
            if (manual.id === 'admin') return isAdminOrEditor;
            if (manual.id === 'manager') return isManager;
            return true;
        });

    }, [user]);

    if (!isOpen) return null;

    return (
        <>
            <Dialog 
                open={isOpen} 
                onClose={handleCloseAttempt} 
                maxWidth="lg" 
                fullWidth 
                PaperProps={{ 
                    sx: { 
                        borderRadius: '20px', 
                        overflow: 'hidden',
                        background: '#f8fafc'
                    } 
                }}
            >
                {/* Header */}
                <Box sx={{ 
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', 
                    color: 'white', 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '4px solid #ff834f'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <BookIcon sx={{ fontSize: 32, color: '#ff834f' }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                Manuais do Sistema
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Baixe os guias em PDF para aprender a utilizar a plataforma.
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={handleCloseAttempt} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                        <CloseIcon data-testid="CloseIcon" />
                    </IconButton>
                </Box>

                {/* Content */}
                <DialogContent sx={{ p: 4 }}>
                    <Grid container spacing={4} justifyContent="center">
                        {allowedManuals.map((manual) => (
                            <Grid item xs={12} md={allowedManuals.length === 1 ? 12 : 6} lg={4} key={manual.id}>
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        p: 3, 
                                        height: '100%', 
                                        borderRadius: '16px', 
                                        border: '1px solid #e2e8f0',
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    {/* Capa do Manual */}
                                    {/* ✅ AJUSTE DE ESCALA:
                                        - width: '100%' para se adaptar ao card
                                        - maxWidth: '280px' para não ficar gigante na tela
                                        - aspectRatio: '512/800' garante a PROPORÇÃO exata da imagem original
                                    */}
                                    <Box 
                                        sx={{ 
                                            width: '100%', 
                                            maxWidth: '280px',   // Define um tamanho visual máximo agradável
                                            aspectRatio: '512/800', // Força a proporção matemática da imagem original
                                            borderRadius: '12px', 
                                            overflow: 'hidden',
                                            mb: 3,
                                            position: 'relative',
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                            cursor: 'pointer',
                                            bgcolor: '#e2e8f0',
                                            mx: 'auto'
                                        }}
                                        onClick={() => window.open(manual.pdfUrl, '_blank')}
                                    >
                                        <img 
                                            src={manual.coverUrl} 
                                            alt={manual.title} 
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover' // Garante que preencha a área sem distorcer
                                            }} 
                                        />
                                        <Box sx={{
                                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                            bgcolor: 'rgba(0,0,0,0.5)', opacity: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'opacity 0.2s',
                                            '&:hover': { opacity: 1 }
                                        }}>
                                            <DownloadIcon sx={{ color: 'white', fontSize: 50 }} />
                                        </Box>
                                    </Box>

                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', textAlign: 'center', mb: 1, lineHeight: 1.2 }}>
                                        {manual.title}
                                    </Typography>
                                    
                                    <Typography variant="caption" sx={{ color: '#64748b', textAlign: 'center', mb: 3, flexGrow: 1, fontSize: '0.85rem' }}>
                                        {manual.description}
                                    </Typography>

                                    <Button 
                                        variant="contained" 
                                        fullWidth
                                        startIcon={<DownloadIcon />}
                                        href={manual.pdfUrl}
                                        target="_blank"
                                        sx={{ 
                                            bgcolor: manual.color, 
                                            color: '#1e293b', 
                                            fontWeight: 'bold',
                                            borderRadius: '8px',
                                            py: 1.5,
                                            '&:hover': { filter: 'brightness(0.95)', bgcolor: manual.color }
                                        }}
                                    >
                                        Baixar PDF
                                    </Button>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {isAutoOpen && (
                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={dontShowAgain}
                                        onChange={(e) => setDontShowAgain(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Não mostrar este aviso automaticamente novamente"
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Aviso de Fechamento */}
            <Dialog open={showWarning} onClose={() => setShowWarning(false)}>
                <Box sx={{ p: 3, maxWidth: 400, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Dica Importante!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Você pode baixar estes manuais a qualquer momento clicando nos botões de ajuda <strong style={{color: '#f59e0b'}}>(?)</strong> nas páginas do sistema.
                    </Typography>
                    <Button onClick={confirmClose} variant="contained" fullWidth>
                        Entendi, fechar
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default ManualsModal;